import { action } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import type { PlanbookSummary } from "@shared/models/planbook"
import type { UserSummary } from "@shared/models/user"
import HasManyAltogether from "cc-frontend/helpers/has-many-altogether"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import fade from "ember-animated/transitions/fade"
import { use } from "ember-could-get-used-to-this"
import { isNil } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import { tracked } from "tracked-built-ins"

export interface ShareUnsharePlanbooksSignature {
  Args: {
    model: {
      userSummary: UserSummary
      parentGroup: Group | null
      group: Group
      teamIds: Array<string>
    }
    isSidebar: boolean
    isHalfScreen?: boolean
    order: number
    closeModal: () => {}
    noBackground: boolean
  }
}

export default class ShareUnsharePlanbooks extends Component<ShareUnsharePlanbooksSignature> {
  @service("ember-store") declare store: EmberStore
  @service declare rpc: Rpc
  @service declare modalManager: ModalManager

  fade = fade

  @tracked pendingPermissionPlanbookId: null | string = null
  @tracked isRemovingCollaboratorId: null | string = null

  @use planbookSummaries = new HasManyAltogether<PlanbookSummary>(() => [
    this.args.model.userSummary,
    "planbooks",
    "planbook-summary",
  ])

  get groupedPlanbooks() {
    if (this.planbookSummaries.isPending) return
    if (isNil(this.planbookSummaries.content)) return []
    return R.pipe(
      this.planbookSummaries.content,
      R.groupBy((planbook) => planbook.attributes.schoolYearId),
      R.toPairs,
      R.map(([schoolYearId, planbooks]) => {
        return { schoolYearId, planbooks }
      }),
      R.sortBy((group) => group.schoolYearId),
      R.reverse()
    )
  }

  @action
  selectNewTeam(planbookId: string, groupId: string) {
    let select = document.querySelector<HTMLOptionElement>(`#share-planbook-${planbookId}`)
    // We should rmove this part regardless
    if (select) select.selected = true
    this.pendingPermissionPlanbookId = planbookId
    this.rpc
      .planbook_collaboration_add_group({
        planbookId: planbookId,
        groupId: groupId,
        role: "VIEWER",
      })
      .then(() => {
        later(() => {
          if (this.isDestroyed || this.isDestroying) return
          // Not entirely sure why we're doing this later. Might be so the model has a chance to update?
          this.pendingPermissionPlanbookId = null
        }, 250)
      })
  }

  @action
  removeCollaborator(planbookId, collaboratorId) {
    this.isRemovingCollaboratorId = collaboratorId
    this.rpc
      .planbook_collaboration_remove_collaborator({
        planbookId: planbookId,
        collaboratorId: collaboratorId,
      })
      .then(() => {
        later(() => {
          if (this.isDestroyed || this.isDestroying) return
          // Not entirely sure why we're doing this later. Might be so the model has a chance to update?
          this.isRemovingCollaboratorId = null
        }, 250)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::ShareUnsharePlanbooks": typeof ShareUnsharePlanbooks
  }
}
